import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { adminApi } from '../api';
import FEATURE_FLAGS from '../config/featureFlags';
import { formatDate } from '../utils/formatDate';

function AdminNav({ tab, setTab, onLogout, alertCount }) {
  return (
    <div style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)', padding: '0 24px', position: 'sticky', top: 0, zIndex: 100 }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 56 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 32 }}>
          <span style={{ fontWeight: 700, fontSize: 18, color: 'var(--primary)' }}>LoopVisitor Admin</span>
          <nav style={{ display: 'flex', gap: 4 }}>
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'alerts', label: 'Alerts', badge: alertCount },
              { id: 'tickets', label: 'Tickets' },
              { id: 'demos', label: 'Demos' },
              { id: 'users', label: 'Users' },
            ].map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)}
                style={{ padding: '8px 16px', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', background: tab === t.id ? 'rgba(108,92,231,0.1)' : 'transparent', color: tab === t.id ? 'var(--primary)' : 'var(--text-secondary)', position: 'relative' }}>
                {t.label}
                {t.badge > 0 && (
                  <span style={{ position: 'absolute', top: 2, right: 2, background: '#ef4444', color: '#fff', fontSize: 10, fontWeight: 700, borderRadius: '50%', width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {t.badge > 99 ? '99+' : t.badge}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>
        <button onClick={onLogout} className="btn btn-secondary btn-sm">Logout</button>
      </div>
    </div>
  );
}

function OverviewTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminApi.getOverview().then(setData).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}><span className="spinner" /></div>;
  if (!data) return <p>Failed to load overview.</p>;

  return (
    <div>
      <h2 style={{ marginBottom: 24 }}>Dashboard Overview</h2>
      <div className="stats-grid">
        <div className="stat-card"><div className="stat-icon">👥</div><div className="stat-value">{data.totalUsers}</div><div className="stat-label">Total Users</div></div>
        <div className="stat-card"><div className="stat-icon">🎫</div><div className="stat-value">{data.totalTickets}</div><div className="stat-label">Total Tickets</div></div>
        <div className="stat-card"><div className="stat-icon">🔴</div><div className="stat-value">{data.openTickets}</div><div className="stat-label">Open Tickets</div></div>
        <div className="stat-card"><div className="stat-icon">📅</div><div className="stat-value">{data.totalDemos}</div><div className="stat-label">Demo Requests</div></div>
        {FEATURE_FLAGS.whatsappEnabled && <div className="stat-card"><div className="stat-icon">📱</div><div className="stat-value">{data.whatsappEnabledTenants || 0}</div><div className="stat-label">WhatsApp Tenants</div></div>}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginTop: 24 }}>
        <div className="card">
          <h3 style={{ marginBottom: 16 }}>Plan Distribution</h3>
          {Object.entries(data.planDistribution || {}).map(([plan, count]) => (
            <div key={plan} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
              <span style={{ textTransform: 'capitalize', fontWeight: 500 }}>{plan}</span>
              <span className="badge badge-purple">{count}</span>
            </div>
          ))}
        </div>
        <div className="card">
          <h3 style={{ marginBottom: 16 }}>Users by Country</h3>
          {Object.entries(data.countryDistribution || {}).length > 0
            ? Object.entries(data.countryDistribution).sort((a, b) => b[1] - a[1]).map(([country, count]) => (
              <div key={country} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                <span style={{ fontWeight: 500 }}>{country}</span>
                <span className="badge badge-info">{count}</span>
              </div>
            ))
            : <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>No country data yet</p>
          }
        </div>
      </div>

      <div className="card" style={{ marginTop: 24 }}>
        <h3 style={{ marginBottom: 16 }}>Recent Signups</h3>
        {data.recentUsers.map((u) => (
          <div key={u.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
            <div>
              <span>{u.email}</span>
              {u.business?.country && <span style={{ color: 'var(--text-muted)', marginLeft: 8 }}>({u.business.country})</span>}
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span className="badge badge-purple" style={{ textTransform: 'capitalize' }}>{u.plan}</span>
              <span style={{ color: 'var(--text-muted)' }}>{formatDate(u.createdAt)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AlertsTab() {
  const [alerts, setAlerts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: 'active', severity: '', page: 1 });
  const [resolving, setResolving] = useState(null);

  const loadAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminApi.getAlerts(filters);
      setAlerts(data.alerts);
      setTotal(data.total);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { loadAlerts(); }, [loadAlerts]);

  const handleAcknowledge = async (id) => {
    setResolving(id);
    try {
      await adminApi.acknowledgeAlert(id);
      loadAlerts();
    } catch (err) { console.error(err); }
    finally { setResolving(null); }
  };

  const handleResolve = async (id) => {
    setResolving(id);
    try {
      await adminApi.resolveAlert(id);
      loadAlerts();
    } catch (err) { console.error(err); }
    finally { setResolving(null); }
  };

  const handleResolveAll = async () => {
    if (!window.confirm('Resolve all active alerts?')) return;
    try {
      await adminApi.resolveAllAlerts(filters.severity || undefined);
      loadAlerts();
    } catch (err) { console.error(err); }
  };

  const severityColor = {
    critical: { bg: '#fef2f2', border: '#fca5a5', text: '#dc2626', badge: 'badge-warning' },
    high: { bg: '#fffbeb', border: '#fcd34d', text: '#d97706', badge: 'badge-warning' },
    medium: { bg: '#eff6ff', border: '#93c5fd', text: '#2563eb', badge: 'badge-info' },
    info: { bg: '#f0fdf4', border: '#86efac', text: '#16a34a', badge: 'badge-success' },
  };

  const severityIcon = { critical: '\u{1F6A8}', high: '\u{26A0}\u{FE0F}', medium: '\u{1F4CB}', info: '\u{2139}\u{FE0F}' };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2>System Alerts ({total})</h2>
        {alerts.length > 0 && filters.status === 'active' && (
          <button onClick={handleResolveAll} className="btn btn-secondary btn-sm">Resolve All</button>
        )}
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <select value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value, page: 1 }))} style={{ padding: '8px 12px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: 13 }}>
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="acknowledged">Acknowledged</option>
          <option value="resolved">Resolved</option>
        </select>
        <select value={filters.severity} onChange={(e) => setFilters((f) => ({ ...f, severity: e.target.value, page: 1 }))} style={{ padding: '8px 12px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: 13 }}>
          <option value="">All Severity</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="info">Info</option>
        </select>
      </div>

      {loading ? <div style={{ textAlign: 'center', padding: 40 }}><span className="spinner" /></div> : alerts.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 48 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>{'\u2705'}</div>
          <h3 style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>No alerts</h3>
          <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>All systems are operating normally.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {alerts.map((alert) => {
            const colors = severityColor[alert.severity] || severityColor.medium;
            let metadata = null;
            try { metadata = alert.metadata ? JSON.parse(alert.metadata) : null; } catch (e) { /* ignore */ }

            return (
              <div key={alert.id} style={{ background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 10, padding: '16px 20px', borderLeft: `4px solid ${colors.text}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <span style={{ fontSize: 16 }}>{severityIcon[alert.severity]}</span>
                      <span style={{ fontWeight: 700, fontSize: 14, color: colors.text }}>{alert.severity.toUpperCase()}</span>
                      <span style={{ fontSize: 11, color: 'var(--text-muted)', background: 'rgba(0,0,0,0.05)', padding: '2px 8px', borderRadius: 4 }}>{alert.type}</span>
                      {alert.status === 'acknowledged' && <span className="badge badge-info" style={{ fontSize: 10 }}>Acknowledged</span>}
                    </div>
                    <h4 style={{ margin: '0 0 4px', fontSize: 15, color: 'var(--text-primary)' }}>{alert.title}</h4>
                    <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>{alert.message}</p>
                    {metadata && (
                      <details style={{ marginTop: 8 }}>
                        <summary style={{ fontSize: 12, color: 'var(--text-muted)', cursor: 'pointer' }}>Details</summary>
                        <pre style={{ fontSize: 11, color: 'var(--text-secondary)', background: 'rgba(0,0,0,0.03)', padding: 8, borderRadius: 4, marginTop: 4, overflow: 'auto', maxHeight: 120 }}>{JSON.stringify(metadata, null, 2)}</pre>
                      </details>
                    )}
                    <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-muted)' }}>
                      {formatDate(alert.createdAt)}
                      {alert.notifiedAt && <span> &middot; Email sent</span>}
                    </div>
                  </div>
                  {alert.status !== 'resolved' && (
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                      {alert.status === 'active' && (
                        <button onClick={() => handleAcknowledge(alert.id)} disabled={resolving === alert.id} className="btn btn-secondary btn-sm" style={{ fontSize: 11, padding: '4px 10px' }}>
                          Ack
                        </button>
                      )}
                      <button onClick={() => handleResolve(alert.id)} disabled={resolving === alert.id} className="btn btn-primary btn-sm" style={{ fontSize: 11, padding: '4px 10px' }}>
                        Resolve
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {total > 50 && (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 16 }}>
          <button disabled={filters.page <= 1} onClick={() => setFilters((f) => ({ ...f, page: f.page - 1 }))} className="btn btn-secondary btn-sm">Previous</button>
          <span style={{ padding: '6px 12px', fontSize: 13 }}>Page {filters.page}</span>
          <button disabled={alerts.length < 50} onClick={() => setFilters((f) => ({ ...f, page: f.page + 1 }))} className="btn btn-secondary btn-sm">Next</button>
        </div>
      )}
    </div>
  );
}

function TicketsTab() {
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', priority: '', search: '', page: 1 });
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [reply, setReply] = useState('');
  const [replying, setReplying] = useState(false);

  const loadTickets = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminApi.getTickets(filters);
      setTickets(data.tickets);
      setTotal(data.total);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { loadTickets(); }, [loadTickets]);

  const openTicket = async (id) => {
    try {
      const data = await adminApi.getTicket(id);
      setSelectedTicket(data.ticket);
    } catch (err) { console.error(err); }
  };

  const handleReply = async () => {
    if (!reply.trim()) return;
    setReplying(true);
    try {
      await adminApi.replyToTicket(selectedTicket.id, reply);
      setReply('');
      openTicket(selectedTicket.id);
      loadTickets();
    } catch (err) { console.error(err); }
    finally { setReplying(false); }
  };

  const updateStatus = async (status) => {
    try {
      await adminApi.updateTicket(selectedTicket.id, { status });
      openTicket(selectedTicket.id);
      loadTickets();
    } catch (err) { console.error(err); }
  };

  const updatePriority = async (priority) => {
    try {
      await adminApi.updateTicket(selectedTicket.id, { priority });
      openTicket(selectedTicket.id);
      loadTickets();
    } catch (err) { console.error(err); }
  };

  if (selectedTicket) {
    return (
      <div>
        <button onClick={() => setSelectedTicket(null)} style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, marginBottom: 16, padding: 0 }}>
          &larr; Back to tickets
        </button>
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <h2 style={{ fontSize: 18, marginBottom: 4 }}>#{selectedTicket.id} — {selectedTicket.subject}</h2>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                {selectedTicket.name} ({selectedTicket.email}){selectedTicket.phone ? ` · ${selectedTicket.phone}` : ''}
              </p>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <select value={selectedTicket.status} onChange={(e) => updateStatus(e.target.value)} style={{ padding: '6px 12px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12, fontFamily: 'inherit' }}>
                <option value="open">Open</option>
                <option value="pending">Pending</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
              <select value={selectedTicket.priority} onChange={(e) => updatePriority(e.target.value)} style={{ padding: '6px 12px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12, fontFamily: 'inherit' }}>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
          </div>
        </div>

        {/* Messages thread */}
        <div className="card" style={{ marginBottom: 16, maxHeight: 400, overflowY: 'auto' }}>
          {selectedTicket.messages && selectedTicket.messages.map((msg) => (
            <div key={msg.id} style={{ marginBottom: 16, padding: 12, borderRadius: 8, background: msg.senderType === 'admin' ? 'rgba(108,92,231,0.05)' : 'var(--bg)', borderLeft: `3px solid ${msg.senderType === 'admin' ? 'var(--primary)' : 'var(--success)'}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: msg.senderType === 'admin' ? 'var(--primary)' : 'var(--success)' }}>
                  {msg.senderType === 'admin' ? 'Admin' : selectedTicket.name}
                </span>
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{formatDate(msg.createdAt)}</span>
              </div>
              <p style={{ fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{msg.message}</p>
            </div>
          ))}
        </div>

        {/* Reply box */}
        <div className="card">
          <textarea value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Type your reply..." rows={3} style={{ width: '100%', padding: '10px 14px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', resize: 'vertical', marginBottom: 12 }} />
          <button onClick={handleReply} disabled={replying || !reply.trim()} className="btn btn-primary">
            {replying ? <span className="spinner" /> : 'Send Reply'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2 style={{ marginBottom: 16 }}>Support Tickets ({total})</h2>
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <input placeholder="Search tickets..." value={filters.search} onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value, page: 1 }))} style={{ padding: '8px 14px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: 13, width: 200 }} />
        <select value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value, page: 1 }))} style={{ padding: '8px 12px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: 13 }}>
          <option value="">All Status</option>
          <option value="open">Open</option>
          <option value="pending">Pending</option>
          <option value="resolved">Resolved</option>
          <option value="closed">Closed</option>
        </select>
        <select value={filters.priority} onChange={(e) => setFilters((f) => ({ ...f, priority: e.target.value, page: 1 }))} style={{ padding: '8px 12px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: 13 }}>
          <option value="">All Priority</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
      </div>

      {loading ? <div style={{ textAlign: 'center', padding: 40 }}><span className="spinner" /></div> : (
        <div className="card" style={{ padding: 0 }}>
          <div className="table-container">
            <table>
              <thead><tr>
                <th>ID</th><th>Subject</th><th>User</th><th>Status</th><th>Priority</th><th>Updated</th>
              </tr></thead>
              <tbody>
                {tickets.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)' }}>No tickets found</td></tr>}
                {tickets.map((t) => (
                  <tr key={t.id} onClick={() => openTicket(t.id)} style={{ cursor: 'pointer' }}>
                    <td>#{t.id}</td>
                    <td style={{ fontWeight: 500 }}>{t.subject}</td>
                    <td style={{ fontSize: 13 }}>{t.name}<br /><span style={{ color: 'var(--text-muted)' }}>{t.email}</span></td>
                    <td><span className={`badge ${t.status === 'open' ? 'badge-warning' : t.status === 'resolved' ? 'badge-success' : 'badge-info'}`}>{t.status}</span></td>
                    <td><span className={`badge ${t.priority === 'high' ? 'badge-warning' : t.priority === 'low' ? 'badge-info' : 'badge-purple'}`}>{t.priority}</span></td>
                    <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{formatDate(t.updatedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function DemosTab() {
  const [demos, setDemos] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [bookingStatus, setBookingStatus] = useState('');
  const [page, setPage] = useState(1);

  const loadDemos = useCallback(() => {
    setLoading(true);
    adminApi.getDemos({ page, search, bookingStatus }).then((data) => { setDemos(data.demos); setTotal(data.total); }).catch(console.error).finally(() => setLoading(false));
  }, [page, search, bookingStatus]);

  useEffect(() => { loadDemos(); }, [loadDemos]);

  const updateDemoStatus = async (id, status) => {
    try {
      await adminApi.updateDemo(id, { bookingStatus: status });
      loadDemos();
    } catch (err) { console.error(err); }
  };

  const bookingStatusColor = {
    pending: 'badge-warning',
    scheduled: 'badge-info',
    completed: 'badge-success',
    cancelled: 'badge-purple',
  };

  return (
    <div>
      <h2 style={{ marginBottom: 16 }}>Demo Requests ({total})</h2>
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <input placeholder="Search demos..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} style={{ padding: '8px 14px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: 13, width: 250 }} />
        <select value={bookingStatus} onChange={(e) => { setBookingStatus(e.target.value); setPage(1); }} style={{ padding: '8px 12px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: 13 }}>
          <option value="">All Status</option>
          <option value="pending">Pending</option>
          <option value="scheduled">Scheduled</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {loading ? <div style={{ textAlign: 'center', padding: 40 }}><span className="spinner" /></div> : (
        <div className="card" style={{ padding: 0 }}>
          <div className="table-container">
            <table>
              <thead><tr><th>Name</th><th>Email</th><th>Phone</th><th>Booking Status</th><th>Calendly</th><th>Requested</th></tr></thead>
              <tbody>
                {demos.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)' }}>No demos found</td></tr>}
                {demos.map((d) => (
                  <tr key={d.id}>
                    <td style={{ fontWeight: 500 }}>{d.name}</td>
                    <td>{d.email}</td>
                    <td>{d.phone || '—'}</td>
                    <td>
                      <select
                        value={d.bookingStatus || 'pending'}
                        onChange={(e) => updateDemoStatus(d.id, e.target.value)}
                        style={{ padding: '4px 8px', border: '1px solid var(--border)', borderRadius: 4, fontSize: 12, fontFamily: 'inherit' }}
                        className={`badge ${bookingStatusColor[d.bookingStatus] || 'badge-warning'}`}
                      >
                        <option value="pending">Pending</option>
                        <option value="scheduled">Scheduled</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </td>
                    <td>
                      {d.calendlyLink ? (
                        <a href={d.calendlyLink} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)', fontSize: 12 }}>View</a>
                      ) : '—'}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{formatDate(d.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {total > 20 && (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 16 }}>
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="btn btn-secondary btn-sm">Previous</button>
          <span style={{ padding: '6px 12px', fontSize: 13 }}>Page {page}</span>
          <button disabled={demos.length < 20} onClick={() => setPage((p) => p + 1)} className="btn btn-secondary btn-sm">Next</button>
        </div>
      )}
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [plan, setPlan] = useState('');
  const [page, setPage] = useState(1);
  const [planUpdating, setPlanUpdating] = useState(null);

  const loadUsers = useCallback(() => {
    setLoading(true);
    adminApi.getUsers({ page, search, plan }).then((data) => { setUsers(data.users); setTotal(data.total); }).catch(console.error).finally(() => setLoading(false));
  }, [page, search, plan]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handlePlanChange = async (userId, newPlan) => {
    setPlanUpdating(userId);
    try {
      await adminApi.updateUserPlan(userId, newPlan);
      loadUsers();
    } catch (err) {
      console.error(err);
    } finally {
      setPlanUpdating(null);
    }
  };

  return (
    <div>
      <h2 style={{ marginBottom: 16 }}>Users ({total})</h2>
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <input placeholder="Search by email..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} style={{ padding: '8px 14px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: 13, width: 250 }} />
        <select value={plan} onChange={(e) => { setPlan(e.target.value); setPage(1); }} style={{ padding: '8px 12px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: 13 }}>
          <option value="">All Plans</option>
          <option value="free">Free</option>
          <option value="starter">Starter</option>
          <option value="growth">Growth</option>
          <option value="pro">Pro</option>
          <option value="custom">Custom</option>
        </select>
      </div>

      {loading ? <div style={{ textAlign: 'center', padding: 40 }}><span className="spinner" /></div> : (
        <div className="card" style={{ padding: 0 }}>
          <div className="table-container">
            <table>
              <thead><tr><th>Email</th><th>Plan</th><th>Business</th><th>Country</th><th>Verified</th><th>Joined</th><th>Actions</th></tr></thead>
              <tbody>
                {users.length === 0 && <tr><td colSpan={7} style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)' }}>No users found</td></tr>}
                {users.map((u) => (
                  <tr key={u.id}>
                    <td>{u.email}</td>
                    <td><span className="badge badge-purple" style={{ textTransform: 'capitalize' }}>{u.plan}</span></td>
                    <td>{u.business?.name || '—'}</td>
                    <td>{u.business?.country || '—'}</td>
                    <td>{u.emailVerified ? <span className="badge badge-success">Yes</span> : <span className="badge badge-warning">No</span>}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{formatDate(u.createdAt)}</td>
                    <td>
                      <select
                        value={u.plan}
                        onChange={(e) => handlePlanChange(u.id, e.target.value)}
                        disabled={planUpdating === u.id}
                        style={{ padding: '4px 8px', border: '1px solid var(--border)', borderRadius: 4, fontSize: 12, fontFamily: 'inherit' }}
                      >
                        <option value="free">Free</option>
                        <option value="starter">Starter</option>
                        <option value="growth">Growth</option>
                        <option value="pro">Pro</option>
                        <option value="custom">Custom</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {total > 20 && (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 16 }}>
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="btn btn-secondary btn-sm">Previous</button>
          <span style={{ padding: '6px 12px', fontSize: 13 }}>Page {page}</span>
          <button disabled={users.length < 20} onClick={() => setPage((p) => p + 1)} className="btn btn-secondary btn-sm">Next</button>
        </div>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const [tab, setTab] = useState('overview');
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [alertCount, setAlertCount] = useState(0);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin-login');
      return;
    }
    adminApi.getMe()
      .then(() => setAuthed(true))
      .catch(() => { localStorage.removeItem('adminToken'); navigate('/admin-login'); })
      .finally(() => setLoading(false));
  }, [navigate]);

  useEffect(() => {
    if (!authed) return;
    adminApi.getAlertSummary().then((data) => setAlertCount(data.total || 0)).catch(() => {});
    const interval = setInterval(() => {
      adminApi.getAlertSummary().then((data) => setAlertCount(data.total || 0)).catch(() => {});
    }, 60000);
    return () => clearInterval(interval);
  }, [authed]);

  useEffect(() => {
    const t = searchParams.get('tab');
    if (t && ['overview', 'alerts', 'tickets', 'demos', 'users'].includes(t)) setTab(t);
  }, [searchParams]);

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin-login');
  };

  if (loading || !authed) return <div className="loading-screen">Loading...</div>;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      <AdminNav tab={tab} setTab={setTab} onLogout={handleLogout} alertCount={alertCount} />
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: 32 }}>
        {tab === 'overview' && <OverviewTab />}
        {tab === 'alerts' && <AlertsTab />}
        {tab === 'tickets' && <TicketsTab />}
        {tab === 'demos' && <DemosTab />}
        {tab === 'users' && <UsersTab />}
      </div>
    </div>
  );
}
